/**
 * Which payment methods a booking's room ACTUALLY accepts (COL-533).
 *
 * `get_room_payment_methods` resolves room override → apartment → property →
 * platform default and applies the `owner_collects` clamp. The SEPA charge
 * functions ask the same function, so what a dashboard offers cannot
 * disagree with what actually gets charged.
 *
 * Extracted because V2 was deciding this from the wrong thing. Its payments
 * row read `mandateActive` — a USER-level `payment_methods` row that
 * survives a tenancy — and told a tenant whose current booking is
 * owner-collected that rent is collected automatically. A tenant who
 * believes that does not transfer rent they owe.
 *
 * Both dashboards now resolve through here, so the two cannot drift.
 */

import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/lib/supabase";

export type RoomPaymentMethods = {
  stripe_card_enabled: boolean;
  bank_transfer_enabled: boolean;
  sepa_debit_enabled: boolean;
  collection_mode: string;
};

export type EffectivePaymentMethods = {
  /** Raw resolver row, or null until it answers. */
  methods: RoomPaymentMethods | null;
  /** True once the resolver says this room is collected by the owner. */
  ownerCollects: boolean;
  /**
   * An external operator's tenant never pays through CoLiviq's Stripe
   * (COL-353). Defence in depth: the resolver already clamps the flags,
   * and `rent-charge-tick` keeps its own equivalent check too.
   */
  externalOperatorBooking: boolean;
  /**
   * SEPA is genuinely available for THIS booking. False while the resolver
   * is still loading: claiming automatic collection we cannot yet confirm is
   * the failure that matters, so this errs toward "tell them nothing".
   */
  sepaEnabled: boolean;
  cardEnabled: boolean;
  isLoading: boolean;
  /** The resolver failed; callers fall back rather than assert. */
  isError: boolean;
};

export function useEffectivePaymentMethods(
  booking:
    | { rooms?: { id?: number | string } | null; operator_id?: unknown }
    | null
    | undefined
): EffectivePaymentMethods {
  const roomId = booking?.rooms?.id;

  const {
    data: methods,
    isPending: methodsPending,
    isError: methodsError,
  } = useQuery<RoomPaymentMethods | null>({
    queryKey: ["room-payment-methods", roomId],
    enabled: !!roomId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc(
        "get_room_payment_methods",
        { p_room_id: roomId }
      );
      if (error) throw error;
      return Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
    },
  });

  const {
    data: platformOperatorId,
    isPending: operatorPending,
    isError: operatorError,
  } = useQuery<string | null>({
    queryKey: ["platform-operator-id"],
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc(
        "platform_operator_id"
      );
      if (error) throw error;
      return (data as string | null) ?? null;
    },
  });

  const ownerCollects = methods?.collection_mode === "owner_collects";

  /*
    Keyed off `is_platform` via the helper RPC, never a literal UUID.

    FAILS CLOSED. This used to require `!!platformOperatorId`, so while that
    query was in flight -- or after it failed -- an external operator's
    booking read as NOT external. The room resolver answers separately and
    usually first, so `cardEnabled` went true on a booking we must never
    collect, and Pay now appeared: the tenant's rent would settle into
    CoLiviq's Stripe while they owe a different party (COL-353).

    When the booking names an operator and we cannot yet say whether it is
    ours, the answer is "assume external", not "assume ours". The cost of
    being wrong that way is a Pay now button missing for a few hundred
    milliseconds; the cost the other way is money in the wrong account.
  */
  const operatorResolved =
    !operatorPending && !operatorError && !!platformOperatorId;
  const externalOperatorBooking =
    !!booking?.operator_id &&
    (!operatorResolved || booking.operator_id !== platformOperatorId);

  return {
    methods: methods ?? null,
    ownerCollects,
    externalOperatorBooking,
    sepaEnabled:
      methods?.sepa_debit_enabled === true &&
      !ownerCollects &&
      !externalOperatorBooking,
    cardEnabled:
      methods?.stripe_card_enabled === true &&
      !ownerCollects &&
      !externalOperatorBooking,
    /*
      Both queries, not just the resolver. A caller that gates on isLoading
      was still rendering while the operator check was outstanding -- which
      is the window the bug above lived in.
    */
    isLoading: (!!roomId && methodsPending) || operatorPending,
    isError: methodsError || operatorError,
  };
}
